/**
 * AviFilters — Barre de filtres compacte AVI (2 lignes).
 *
 * Spec iter176 (2026-02-27) — refonte compacte :
 *   • Ligne 1 : recherche seule, pleine largeur
 *   • Ligne 2 : Statut / Priorité / Demandé par (Shadcn Selects)
 *               | séparateur | Période (pastille ronde + popover)
 *               | séparateur | 3 chips rapides en pastilles rondes
 *
 * Comportement pastilles rondes (Période + chips) :
 *   • État inactif normal : icône seule, ronde 30 px
 *   • Hover OU actif : la pastille s'étire pour révéler son libellé
 *   • Mouseleave + inactif : recontraction icône seule
 */

import { Fragment, useEffect, useRef, useState } from "react";
import {
  Search, X, User2, Zap, Eye, MessageSquare, Calendar, MapPin,
} from "lucide-react";
import SearchClearButton from "@/components/ui/SearchClearButton";
import SearchDebounceBar from "@/components/ui/SearchDebounceBar";
import { J3Select } from "@/components/common/ProjectJ3";
import {
  Select as ShadSelect,
  SelectContent as ShadSelectContent,
  SelectItem as ShadSelectItem,
  SelectTrigger as ShadSelectTrigger,
  SelectValue as ShadSelectValue,
} from "@/components/ui/select";

const ALL_SENTINEL = "__all__";

/* --------------------------------------------------------------------------
 * FilterSelect — Wrapper Shadcn Select (dropdown Statut / Priorité / Demandé).
 * Sentinel `__all__` = valeur « Tous ».
 * ------------------------------------------------------------------------ */
function FilterSelect({ value, onChange, options, testid, icon }) {
  const active = !!value;
  const current = options.find((o) => o.key === value) || options[0] || null;
  const uiValue = value === "" || value == null ? ALL_SENTINEL : value;
  const handle = (v) => onChange(v === ALL_SENTINEL ? "" : v);
  return (
    <ShadSelect value={uiValue} onValueChange={handle}>
      <ShadSelectTrigger
        className="h-9 rounded-md text-[12px] font-medium gap-2 transition-colors"
        style={{
          background: "#212121",
          border: `1px solid ${active ? "#EC4899" : "#383838"}`,
          color: active ? "#F472B6" : "#D1D5DB",
          minWidth: 160,
        }}
        data-testid={testid}
      >
        {icon && (
          <span style={{ color: active ? "#F472B6" : "#9CA3AF", display: "inline-flex" }}>
            {icon}
          </span>
        )}
        <ShadSelectValue placeholder={current?.label || ""}>{current?.label || ""}</ShadSelectValue>
      </ShadSelectTrigger>
      <ShadSelectContent
        className="border-0"
        style={{ background: "#1B1B1B", border: "1px solid #2A2A2A", color: "#EDEDED" }}
        data-testid={`${testid}-menu`}
      >
        {options.map((o) => (
          <Fragment key={o.key}>
          <ShadSelectItem
            value={o.key === "" ? ALL_SENTINEL : o.key}
            className="text-[12px] focus:bg-white/[0.06] cursor-pointer"
            style={{ color: "#EDEDED" }}
            data-testid={`${testid}-opt-${o.key || "all"}`}
          >
            {o.label}
          </ShadSelectItem>
          {o.sepAfter && <div className="border-t border-white/10 my-1" data-testid={`${testid}-siege-sep`} />}
          </Fragment>
        ))}
      </ShadSelectContent>
    </ShadSelect>
  );
}

/* --------------------------------------------------------------------------
 * ExpandingPill — Pastille ronde qui s'étire au hover ou quand active.
 * Utilisée pour les chips rapides (Immédiat/Urgent, En copie/Témoin, À lire).
 * ------------------------------------------------------------------------ */
function ExpandingPill({ Icon, label, color, active, onClick, testid, title }) {
  const [hover, setHover] = useState(false);
  const expanded = active || hover;
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="inline-flex items-center transition-all overflow-hidden whitespace-nowrap"
      style={{
        height: 30,
        borderRadius: 15,
        // Padding varie selon expanded pour que le contenu reste centré.
        paddingLeft: expanded ? 10 : 8,
        paddingRight: expanded ? 12 : 8,
        gap: expanded ? 6 : 0,
        background: active ? `${color}22` : "#1A1A1A",
        border: `1px solid ${active ? `${color}66` : "#2A2A2A"}`,
        color: active ? color : "#9CA3AF",
        fontSize: 11,
        fontWeight: active ? 600 : 500,
        maxWidth: expanded ? 260 : 30,
        transitionDuration: "180ms",
        transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
        transitionProperty: "max-width, padding, background, border-color, color, gap",
        cursor: "pointer",
      }}
      title={title || label}
      data-testid={testid}
    >
      <Icon size={13} strokeWidth={2.4} style={{ color, flexShrink: 0 }} />
      <span
        className="inline-block overflow-hidden"
        style={{
          maxWidth: expanded ? 220 : 0,
          opacity: expanded ? 1 : 0,
          transitionDuration: "180ms",
          transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
          transitionProperty: "max-width, opacity",
        }}
      >
        {label}
      </span>
      {active && expanded && (
        <X
          size={11}
          strokeWidth={2.6}
          className="ml-0.5 opacity-80 flex-shrink-0"
        />
      )}
    </button>
  );
}

/* --------------------------------------------------------------------------
 * PeriodPill — Pastille ronde Période avec popover Du/Au.
 * Même comportement expand-on-hover que ExpandingPill, mais clic ouvre
 * un mini-popover avec les 2 inputs date.
 * ------------------------------------------------------------------------ */
function PeriodPill({ dateFrom, dateTo, setDateFrom, setDateTo, setPage }) {
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const active = !!(dateFrom || dateTo);
  const expanded = active || hover || open;
  const color = "#EC4899";

  // Fermeture au clic à l'extérieur.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const label = active
    ? (dateFrom && dateTo
        ? `${dateFrom} → ${dateTo}`
        : (dateFrom ? `Depuis ${dateFrom}` : `Jusqu'au ${dateTo}`))
    : "Période";

  const clear = (e) => {
    e.stopPropagation();
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  return (
    <div className="relative" ref={anchorRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="inline-flex items-center transition-all overflow-hidden whitespace-nowrap"
        style={{
          height: 30,
          borderRadius: 15,
          paddingLeft: expanded ? 10 : 8,
          paddingRight: expanded ? 12 : 8,
          gap: expanded ? 6 : 0,
          background: active ? `${color}22` : "#1A1A1A",
          border: `1px solid ${active ? `${color}66` : "#2A2A2A"}`,
          color: active ? color : "#9CA3AF",
          fontSize: 11,
          fontWeight: active ? 600 : 500,
          maxWidth: expanded ? 280 : 30,
          transitionDuration: "180ms",
          transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
          transitionProperty: "max-width, padding, background, border-color, color, gap",
          cursor: "pointer",
        }}
        data-testid="avi-chip-period"
        title={label}
      >
        <Calendar size={13} strokeWidth={2.4} style={{ color, flexShrink: 0 }} />
        <span
          className="inline-block overflow-hidden tabular-nums"
          style={{
            maxWidth: expanded ? 240 : 0,
            opacity: expanded ? 1 : 0,
            transitionDuration: "180ms",
            transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
            transitionProperty: "max-width, opacity",
          }}
        >
          {label}
        </span>
        {active && expanded && (
          <span
            role="button"
            aria-label="Effacer la période"
            tabIndex={0}
            onClick={clear}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); clear(e); } }}
            className="inline-flex items-center justify-center rounded-full ml-0.5 hover:bg-white/10 flex-shrink-0"
            style={{ width: 14, height: 14 }}
            data-testid="avi-chip-period-clear"
          >
            <X size={10} strokeWidth={2.4} />
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute z-50 mt-1.5 rounded-lg p-3 shadow-xl"
          style={{
            top: "100%", left: 0, minWidth: 260,
            background: "#1B1B1B", border: "1px solid #2A2A2A",
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid="avi-chip-period-popover"
        >
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Période</div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] w-6" style={{ color: "#9CA3AF" }}>Du</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="flex-1 h-8 rounded-md px-2 text-[12px] tabular-nums"
                style={{
                  background: "#212121", border: "1px solid #383838",
                  color: dateFrom ? "#F472B6" : "#EDEDED", colorScheme: "dark",
                }}
                data-testid="avi-filter-date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] w-6" style={{ color: "#9CA3AF" }}>Au</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="flex-1 h-8 rounded-md px-2 text-[12px] tabular-nums"
                style={{
                  background: "#212121", border: "1px solid #383838",
                  color: dateTo ? "#F472B6" : "#EDEDED", colorScheme: "dark",
                }}
                data-testid="avi-filter-date-to"
              />
            </div>
            {active && (
              <button
                type="button"
                onClick={clear}
                className="w-full h-7 rounded-md text-[11px] font-medium mt-1"
                style={{ background: "#2A1F2A", color: "#F472B6", border: "1px solid #52273f" }}
                data-testid="avi-chip-period-reset"
              >
                Effacer la période
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * VerticalDivider — Fine ligne verticale entre groupes de filtres.
 * ------------------------------------------------------------------------ */
function VerticalDivider() {
  return (
    <span
      aria-hidden
      className="inline-block self-stretch"
      style={{ width: 1, background: "#2A2A2A", margin: "2px 4px" }}
    />
  );
}

/* -------------------------------------------------------------------------- */
export default function AviFilters({
  isInfo, isAccuse,
  q, setQ, debouncedQ,
  statusFilter, setStatusFilter,
  priorityFilter, setPriorityFilter,
  demandeurFilter, setDemandeurFilter,
  projectFilter, setProjectFilter,
  projectsList = [],
  dateFrom, setDateFrom, dateTo, setDateTo,
  setPeriodFilter,
  scope, setScope,
  usersList,
  setPage,
}) {
  return (
    <div
      className="rounded-xl p-3 space-y-2.5"
      style={{ background: "#161616", border: "1px solid #2A2A2A" }}
      data-testid="avi-filters-block"
    >
      {/* --- Ligne 1 : recherche pleine largeur --- */}
      <div className="relative w-full">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          placeholder="Rechercher une demande…"
          className="w-full h-9 pl-9 pr-9 rounded-md text-sm placeholder:text-[#666]"
          style={{ background: "#212121", border: "1px solid #383838", color: "#EDEDED" }}
          data-testid="avi-search"
        />
        <SearchClearButton value={q} onClear={() => { setQ(""); setPage(1); }} testid="avi-search-clear" />
        <SearchDebounceBar
          active={q !== debouncedQ}
          color="#0078D4"
          testid="avi-search-debounce-bar"
        />
      </div>

      {/* --- Ligne 2 : dropdowns + Période + | + chips rapides --- */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="avi-filters-bar">
        <div style={{ width: 170 }}>
        <FilterSelect
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          options={isInfo ? [
            { key: "",                label: "Tous les statuts" },
            { key: "en_attente",      label: "En attente" },
            { key: "partiel_repondu", label: "Partiellement" },
            { key: "lue",             label: "Lue" },
            { key: "annulee",         label: "Annulée" },
          ] : isAccuse ? [
            { key: "",                label: "Tous les statuts" },
            { key: "en_attente",      label: "En attente" },
            { key: "partiel_repondu", label: "Partiellement" },
            { key: "accusee",         label: "Reçu" },
            { key: "annulee",         label: "Annulée" },
          ] : [
            { key: "",                label: "Tous les statuts" },
            { key: "en_attente",      label: "En attente" },
            { key: "partiel_repondu", label: "Partiellement" },
            { key: "approuvee",       label: "Approuvée" },
            { key: "refusee",         label: "Refusée" },
            { key: "annulee",         label: "Annulée" },
          ]}
          testid="avi-filter-status"
        />
        </div>
        <div style={{ width: 170 }}>
        <FilterSelect
          value={priorityFilter}
          onChange={(v) => { setPriorityFilter(v); setPage(1); }}
          options={[
            { key: "",         label: "Toutes priorités" },
            { key: "immediat", label: "Immédiat" },
            { key: "urgent",   label: "Urgent" },
            { key: "moyen",    label: "Moyen" },
            { key: "normal",   label: "Normal" },
          ]}
          testid="avi-filter-priority"
        />
        </div>
        <div style={{ width: 200 }}>
        <FilterSelect
          value={demandeurFilter}
          onChange={(v) => { setDemandeurFilter(v); setPage(1); }}
          icon={<User2 size={13} />}
          options={[
            { key: "", label: "Demandé par (tous)" },
            ...usersList.map((u) => ({
              key: u.id,
              label: `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "—",
            })),
          ]}
          testid="avi-filter-demandeur"
        />
        </div>
        {/* Spec iter253 (2026-03-01) — Filtre Siège / Projet, aligné
            sur le dropdown Documents (comportement identique). */}
        <div style={{ width: 200 }}>
        <J3Select
          projects={projectsList.filter((p) => p.in_choices !== false)}
          value={projectFilter || ""}
          onChange={(v) => { setProjectFilter && setProjectFilter(v || ""); setPage(1); }}
          allLabel="Siège / Projet (tous)"
          testId="avi-filter-project"
          triggerStyle={{ height: 36, background: "#212121", border: `1px solid ${projectFilter ? "#EC4899" : "#383838"}`, color: projectFilter ? "#F472B6" : "#D1D5DB", fontSize: 12, fontWeight: 500, borderRadius: 6 }}
        />
        </div>

        {/* Séparateur | */}
        <VerticalDivider />

        {/* Pastille Période (icône + expand) */}
        <PeriodPill
          dateFrom={dateFrom} dateTo={dateTo}
          setDateFrom={setDateFrom} setDateTo={setDateTo}
          setPage={setPage}
        />

        {/* Séparateur | */}
        <VerticalDivider />

        {/* Chips rapides — pastilles rondes expand-on-hover/active */}
        <ExpandingPill
          Icon={Zap}
          label="Immédiat/Urgent"
          color="#F97316"
          active={scope === "high_priority"}
          onClick={() => { setScope((s) => (s === "high_priority" ? "" : "high_priority")); setPage(1); }}
          testid="avi-chip-high_priority"
          title={scope === "high_priority" ? "Cliquez à nouveau pour désélectionner" : "Filtrer sur Immédiat + Urgent"}
        />
        {/* Spec iter180 pt.3 (2026-02-27) — chip « En copie / Témoin »
            retiré sur Information (rôle Témoin supprimé du sous-type —
            spec iter170). Conservé sur Validation et Accusé. */}
        {!isInfo && (
          <ExpandingPill
            Icon={Eye}
            label="En copie / Témoin"
            color="#84CC16"
            active={scope === "witness"}
            onClick={() => { setScope((s) => (s === "witness" ? "" : "witness")); setPage(1); }}
            testid="avi-chip-witness"
            title={scope === "witness" ? "Cliquez à nouveau pour désélectionner" : "Où je suis témoin"}
          />
        )}
        <ExpandingPill
          Icon={MessageSquare}
          label="À lire"
          color="#F472B6"
          active={scope === "unread"}
          onClick={() => { setScope((s) => (s === "unread" ? "" : "unread")); setPage(1); }}
          testid="avi-chip-unread"
          title={scope === "unread" ? "Cliquez à nouveau pour désélectionner" : "Messages non lus"}
        />

        {/* Reset global — visible seulement si un filtre est appliqué */}
        {(statusFilter || priorityFilter || demandeurFilter || projectFilter || dateFrom || dateTo || scope) && (
          <button
            type="button"
            onClick={() => {
              setStatusFilter(""); setPriorityFilter(""); setDemandeurFilter("");
              setProjectFilter && setProjectFilter("");
              setPeriodFilter("");
              setDateFrom(""); setDateTo(""); setScope("");
              setPage(1);
            }}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-medium ml-auto"
            style={{ background: "#3a1414", color: "#e07a7a", border: "1px solid #5a2020" }}
            data-testid="avi-filters-reset"
          >
            <X size={11} strokeWidth={2.6} /> Effacer tout
          </button>
        )}
      </div>
    </div>
  );
}
